import {
  PRODUCT_CATEGORIES,
  PRODUCT_CATEGORY_LABELS,
  PRODUCT_MENU_GROUPS,
  PRODUCT_MENU_GROUP_LABELS,
} from "@artisancode/api-types";

import { SheetBase } from "@/components/shared/sheet-base";
import { WorkspaceCreateField } from "@/components/shared/workspace-create-field";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { NumberInput } from "@/components/ui/number-input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useNextNumberPreview } from "@/hooks/use-number-sequences";

import { ProductPhotoUpload } from "./product-photo-upload";
import { ProductRecipeSection } from "./product-recipe-section";
import { useProductForm } from "./use-product-form";

import type { Product } from "@artisancode/api-types";

export function ProductDialog({
  open,
  onOpenChange,
  editing,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editing: Product | null;
}) {
  const {
    form,
    recipe,
    isProduced,
    isSubmitting,
    isUploading,
    photoDisplayUrl,
    onSelectPhoto,
    onRemovePhoto,
    onSubmit,
  } = useProductForm(open, editing, () => onOpenChange(false));
  const { preview: sequencePreview, autoGenerate } =
    useNextNumberPreview("product");

  const footer = (
    <>
      <Button
        variant="outline"
        onClick={() => onOpenChange(false)}
        disabled={isSubmitting}
      >
        Batal
      </Button>
      <Button type="submit" form="product-form" disabled={isSubmitting}>
        {isUploading
          ? "Mengunggah foto..."
          : isSubmitting
            ? "Menyimpan..."
            : editing
              ? "Simpan"
              : "Tambah"}
      </Button>
    </>
  );

  return (
    <SheetBase
      open={open}
      onOpenChange={onOpenChange}
      title={editing ? "Edit Produk" : "Tambah Produk"}
      className={isProduced ? "sm:max-w-4xl" : "sm:max-w-md"}
      footer={footer}
    >
      <Form {...form}>
        <form id="product-form" onSubmit={onSubmit} className="grid gap-3 py-2">
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Nama Produk</FormLabel>
                <FormControl>
                  <Input placeholder="Nama produk" autoFocus {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <div className="grid grid-cols-2 gap-3">
            <FormField
              control={form.control}
              name="sku"
              render={({ field }) => {
                const previewSeq =
                  !editing && autoGenerate ? sequencePreview : "";
                return (
                  <FormItem>
                    <FormLabel>Kode / SKU</FormLabel>
                    <FormControl>
                      <Input
                        placeholder={
                          editing
                            ? "SKU Produk"
                            : previewSeq
                              ? `Otomatis: ${previewSeq}`
                              : "Opsional"
                        }
                        {...field}
                        disabled={!!editing || autoGenerate}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                );
              }}
            />
            <FormField
              control={form.control}
              name="category"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Kategori</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Pilih kategori" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {PRODUCT_CATEGORIES.map((cat) => (
                        <SelectItem key={cat} value={cat}>
                          {PRODUCT_CATEGORY_LABELS[cat]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <FormField
            control={form.control}
            name="menuGroup"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Grup Menu</FormLabel>
                <Select onValueChange={field.onChange} value={field.value}>
                  <FormControl>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Pilih grup menu" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {PRODUCT_MENU_GROUPS.map((g) => (
                      <SelectItem key={g} value={g}>
                        {PRODUCT_MENU_GROUP_LABELS[g]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Mengelompokkan pilihan menu di form penjualan & produksi.
                </p>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="sourcingType"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Tipe Pengadaan</FormLabel>
                <Select
                  onValueChange={field.onChange}
                  value={field.value ?? "produced"}
                >
                  <FormControl>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Pilih tipe pengadaan" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="produced">
                      Diproduksi Sendiri (Menu Racikan / Resep Bahan Baku)
                    </SelectItem>
                    <SelectItem value="purchased">
                      Beli Jadi / Titipan (Ready to Sell / Vendor)
                    </SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
          <div className="grid grid-cols-2 gap-3">
            <FormField
              control={form.control}
              name="sellingPrice"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Harga Jual</FormLabel>
                  <FormControl>
                    <NumberInput
                      placeholder="0"
                      value={field.value}
                      onChange={field.onChange}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="minStock"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Batas Minimum Stok (Opsional)</FormLabel>
                  <FormControl>
                    <NumberInput
                      placeholder="Contoh: 5"
                      value={field.value}
                      onChange={field.onChange}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          {isProduced && !!editing?.missingCostItems.length && (
            <p className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-700 dark:bg-amber-950/40 dark:text-amber-400">
              HPP belum lengkap: {editing.missingCostItems.join(", ")} belum ada
              harga. Isi lewat Stok Awal, belanja, atau repacking.
            </p>
          )}
          {isProduced && <ProductRecipeSection recipe={recipe} />}
          <ProductPhotoUpload
            photoUrl={photoDisplayUrl}
            isUploading={isUploading}
            onSelectPhoto={onSelectPhoto}
            onRemovePhoto={onRemovePhoto}
          />
          <FormField
            control={form.control}
            name="notes"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Catatan</FormLabel>
                <FormControl>
                  <Textarea placeholder="Catatan (opsional)" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <WorkspaceCreateField
            control={form.control}
            name="workspaceId"
            editing={!!editing}
          />
        </form>
      </Form>
    </SheetBase>
  );
}
